import React, { useState } from 'react';
import { Trash2, X, Loader2, CheckSquare, Square } from 'lucide-react';

/**
 * Floating "N selected" bar that appears whenever the admin has ticked at
 * least one checkbox in a list. Supports a single primary destructive action
 * (bulk delete) with confirm-once and an explicit "Clear selection" button.
 *
 * Props:
 *   selectedCount: number
 *   totalCount: number
 *   onClear: () => void
 *   onDelete: () => Promise<void>
 *   onSelectAll?: () => void   (optional; renders a "select all visible" link)
 *   entityLabel: string (singular, e.g. 'booking', 'review', 'user')
 *   confirmMessage?: string    custom confirm text
 *   disabledReason?: string    when truthy, disable delete + show tooltip
 *   testIdPrefix: string       e.g. 'admin-bookings-bulk'
 */
export default function BulkActionsBar({
  selectedCount,
  totalCount,
  onClear,
  onDelete,
  onSelectAll,
  entityLabel = 'item',
  confirmMessage,
  disabledReason,
  testIdPrefix,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (selectedCount === 0) return null;

  const handleDelete = async () => {
    setError('');
    const plural = selectedCount === 1 ? entityLabel : `${entityLabel}s`;
    const msg = confirmMessage
      || `Delete ${selectedCount} ${plural}? This cannot be undone from this screen — bookings can still be recovered from the deleted-bookings audit tab.`;
    if (!window.confirm(msg)) return;
    setSubmitting(true);
    try {
      await onDelete();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Bulk delete failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid={`${testIdPrefix}-bar`}
      data-count={selectedCount}
      className="sticky top-0 z-20 mb-3 -mx-2 px-3 py-2.5 bg-slate-900 text-white rounded-sm shadow-md flex items-center gap-3 flex-wrap"
    >
      <div className="flex items-center gap-2 text-sm font-semibold">
        <CheckSquare className="w-4 h-4 text-amber-300" />
        <span data-testid={`${testIdPrefix}-count`}>
          {selectedCount} of {totalCount} selected
        </span>
      </div>
      {onSelectAll && selectedCount < totalCount && (
        <button
          type="button"
          onClick={onSelectAll}
          data-testid={`${testIdPrefix}-select-all`}
          className="text-xs text-slate-300 hover:text-white underline"
        >
          Select all {totalCount}
        </button>
      )}
      <div className="flex-1" />
      {error && (
        <span data-testid={`${testIdPrefix}-error`} className="text-xs text-red-200">
          {error}
        </span>
      )}
      <button
        type="button"
        onClick={handleDelete}
        disabled={submitting || !!disabledReason}
        data-testid={`${testIdPrefix}-delete`}
        title={disabledReason || ''}
        className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        Delete selected
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={submitting}
        data-testid={`${testIdPrefix}-clear`}
        className="px-2 py-1.5 text-xs text-slate-300 hover:text-white inline-flex items-center gap-1 disabled:opacity-50"
      >
        <X className="w-3.5 h-3.5" />
        Clear
      </button>
    </div>
  );
}

/**
 * Tiny checkbox cell helper for tables. Renders an a11y-friendly checkbox
 * matching the bar's styling.
 */
export function RowCheckbox({ checked, onChange, label, testId, disabled }) {
  return (
    <label
      className={`inline-flex items-center cursor-pointer select-none ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      title={label || 'Select row'}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => !disabled && onChange?.(e.target.checked)}
        disabled={disabled}
        className="sr-only"
        aria-label={label || 'Select row'}
      />
      {checked ? (
        <CheckSquare className="w-4 h-4 text-blue-700" />
      ) : (
        <Square className="w-4 h-4 text-slate-400 hover:text-slate-700" />
      )}
    </label>
  );
}
